// SPDX-License-Identifier: AGPL-3.0-or-later

import type {
	ComposerHolidayPeekMachineInput,
	PeekChanceCurve,
} from '@app/features/holiday/components/ComposerHolidayPeekStateMachine';
import type {HolidayPackId} from '@app/features/holiday/state/HolidayPreferences';
import {holidaySeasonPhase} from '@app/features/holiday/utils/holidaySeason';

export interface ComposerHolidayPeekSeasonRates {
	/** Ceiling every chance curve is held to while the pack is outside its peak window. */
	offPeakChanceCap: number;
}

export function capPeekChanceCurve(curve: PeekChanceCurve, pMax: number): PeekChanceCurve {
	return {
		...curve,
		pMin: Math.min(curve.pMin, pMax),
		pMax: Math.min(curve.pMax, pMax),
	};
}

export function mapPeekMachineCurves(
	input: ComposerHolidayPeekMachineInput,
	mapCurve: (curve: PeekChanceCurve) => PeekChanceCurve,
): ComposerHolidayPeekMachineInput {
	return {
		...input,
		rules: input.rules.map((rule) => ({...rule, curve: mapCurve(rule.curve)})),
		spawnCurveAfterHide: mapCurve(input.spawnCurveAfterHide),
		spawnCurveAfterComfort: mapCurve(input.spawnCurveAfterComfort),
		revealCurveAfterComfort: mapCurve(input.revealCurveAfterComfort),
	};
}

/** Calendar only: peak runs a pack's published curves, everything else runs its capped ceiling. */
export function isHolidayPeekFullRate(packId: HolidayPackId, now: Date = new Date()): boolean {
	return holidaySeasonPhase(packId, now) === 'peak';
}

/**
 * Season rule shared by every composer peek pack: full curves at peak, a pack-supplied ceiling
 * before it. The cap value, the art, and the curves themselves stay pack-local.
 */
export function applyComposerHolidayPeekSeasonRates(
	input: ComposerHolidayPeekMachineInput,
	packId: HolidayPackId,
	now: Date,
	rates: ComposerHolidayPeekSeasonRates,
): ComposerHolidayPeekMachineInput {
	if (isHolidayPeekFullRate(packId, now)) return input;
	return mapPeekMachineCurves(input, (curve) => capPeekChanceCurve(curve, rates.offPeakChanceCap));
}
